<template>
  <b-container>
    <template v-if="getSustainabilityData">
      <!-- Sustainability classification pills -->
      <b-row no-gutters>
        <b-col>
          <p class="mt-3 font-weight-bold">{{ $t('sustainability.classification') }}</p>
          <div class="d-flex justify-content-between my-3">
            <div
              v-for="(item, i) in getSustainabilityData.articles"
              :key="i"
              :class="[isOdd(i) ? 'mx-3' : '', 'w-100']"
            >
              <custom-pill
                :value="item.value"
                :status="item.status"
                :text-bold="true"
                :fixed-width="false"
              ></custom-pill>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Sustainability pie-chart & legend -->
      <b-row no-gutters v-if="getSustainabilityData.series != null">
        <b-col col xl="6" lg="12" md="12" sm="12">
          <div class="sust-pie-wrapper">
            <sustainability-pie-chart
              :width="pieChartWidth"
              :height="pieChartHeight"
              :xy-position="['50%', '50%']"
              :value="getSustainabilityData.series"
              :callback="sustainabilityPieChartTooltipFormatter"
            ></sustainability-pie-chart>
          </div>
        </b-col>
        <b-col col xl="6" lg="12" md="12" sm="12">
          <div class="mt-legend mx-auto">
            <div class="d-flex mb-3" v-for="(item, i) in getSustainabilityData.legend" :key="i">
              <div :class="['mr-auto', 'pl-' + item.indentation]">
                <div :class="['mr-1', 'square-shape', 'sust-legend-' + item.key]"></div>
                <span class="sust-chart-legend">
                  {{ $t('sustainability.chart.' + item.key) }}
                </span>
              </div>
              <div class="sust-chart-legend font-weight-bold">{{ item.y + '%' }}</div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Sustainability info -->
      <b-row no-gutters class="my-3" v-if="getSustainabilityData.options != null">
        <b-col>
          <div class="d-flex mb-1" v-for="(item, i) in getSustainabilityData.options" :key="i">
            <div class="flex-grow-1">
              <span class="font-weight-bold align-middle">{{ $t('sustainability.' + item.label) }}</span>
            </div>
            <div class="text-right">
              <b-form-radio v-model="item.value" :value="true" disabled>
                <span :class="[item.value ? '' : 'var(--primary)']">
                  {{ item.value ? $t('common.present') : $t('common.absent') }}
                </span>
              </b-form-radio>
            </div>
          </div>
        </b-col>
      </b-row>
    </template>
    <template v-else>
      <b-row>
        <b-col>
          <no-content
            :icon-name="'exclamation-circle'"
            :icon-class="['mb-3', 'fa-3x']"
            :text="$t('common.noContent')"
          ></no-content>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import CustomPill from '../../components/Lib/CustomPill.vue'
import NoContent from '../../components/Lib/NoContent.vue'
import SustainabilityPieChart from '../../components/ProductDetails/SustainabilityPieChart.vue'
import { isOdd } from '../../utils/generic'

export default {
  components: {
    CustomPill,
    NoContent,
    SustainabilityPieChart,
  },

  props: {
    sustainabilityData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      pieChartWidth: 300,
      pieChartHeight: 330,
      squareSymbol: '&#9632;',
      sfdrArticles: [
        { key: '6', value: 'Art. 6', status: false },
        { key: '8', value: 'Art. 8', status: false },
        { key: '9', value: 'Art. 9', status: false },
      ],
      sfdrOptions: [
        { key: 'esgBenchmark', label: 'benchmarkEsg' },
        { key: 'pai', label: 'paiConsideration' },
      ],
      sfdrChartInnerSize: {
        1: 180,
        2: 100,
        3: 20,
      },
      sfdrChartLevels: {
        1: ['percentageOther', 'keyAllineatiAS'],
        2: ['percentageOther', 'percentageOtherCharacteristicsInvestment', 'keySostenibili'],
        3: [
          'percentageOther',
          'percentageOtherCharacteristicsInvestment',
          'percentageOtherSocialInvestment',
          'percentageOtherEnvironmentalInvestments',
          'percentageOtherTaxonomyAlignedInvestments',
          'percentageNotDefined',
        ],
      },
      sfdrChartItems: [
        { key: 'percentageOther', color: '#93ABDD', indentation: 0 },
        { key: 'keyAllineatiAS', color: '#B7CFA7', indentation: 0 },
        { key: 'percentageOtherCharacteristicsInvestment', color: '#9BBE81', indentation: 4 },
        { key: 'keySostenibili', color: '#79AD47', indentation: 4 },
        { key: 'percentageOtherSocialInvestment', color: '#E3F0D8', indentation: 5 },
        { key: 'percentageOtherEnvironmentalInvestments', color: '#648E3A', indentation: 5 },
        { key: 'percentageOtherTaxonomyAlignedInvestments', color: '#577C33', indentation: 5 },
        { key: 'percentageNotDefined', color: '#D9D9D9', indentation: 5 },
      ],
    }
  },

  computed: {
    getSustainabilityData: {
      get() {
        return this.setSFDRInformation(this.sustainabilityData)
      },
    },
  },

  methods: {
    isOdd,

    findByKey(list, value) {
      return list.find((obj) => obj.key === value)
    },

    sustainabilityPieChartTooltipFormatter(key, val) {
      return (
        '<span class="sust-tooltip-symbol sust-tooltip-' +
        key +
        '">' +
        this.squareSymbol +
        '</span> ' +
        this.$t('sustainability.chart.' + key) +
        ': <b>' +
        val +
        '%</b>'
      )
    },

    setSFDRInformation(list) {
      if (list == null || Object.keys(list).length === 0 || Object.values(list).every((val) => val == null)) return null

      const articles =
        [...this.sfdrArticles].map((a) => {
          return {
            ...a,
            status: list.articleNumber === a.key,
          }
        }) || null

      if (!('otherInfo' in list) || list.otherInfo == null) {
        return {
          articles: articles,
        }
      }

      const options =
        [...this.sfdrOptions].map((o) => {
          return {
            ...o,
            value: o.key in list.otherInfo && list.otherInfo[o.key] != null ? list.otherInfo[o.key] : false,
          }
        }) || null

      let legend = null
      let series = []
      const node = 0
      const total = 100
      const levels = Object.keys(this.sfdrChartLevels)
      levels.forEach((level) => {
        let data = []
        const keys = this.sfdrChartLevels[level]
        keys.forEach((key) => {
          let item = this.findByKey(this.sfdrChartItems, key)
          if (item != null) {
            item.name = key
            const formatted = this.$n(list.otherInfo[key], 'percent').replace('%', '')
            const normalized = formatted.replace(',', '.')
            item.y = Math.round(parseFloat(normalized) * 100) / 100 || node
            data.push(item)
          }
        })
        const sum = [...data].map((item) => item.y).reduce((a, b) => a + b)
        data.map((item) => {
          if (item.y === node) {
            item.y = total - sum
          }
        })
        series.push({
          name: 'node_' + level,
          data: data,
          innerSize: this.sfdrChartInnerSize[level],
        })
      })

      const flatSeries = series.map((s) => s.data).flat()
      legend =
        [...this.sfdrChartItems].map((item) => {
          const match = this.findByKey(flatSeries, item.key)
          if (match) {
            item.y = match.y
          }
          return item
        }) || null

      return {
        articles: articles,
        options: options,
        legend: legend,
        series: series.reverse(),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.sust-pie-wrapper {
  width: 300px;
}

.square-shape {
  width: 10px;
  height: 10px;
  display: inline-block;
  border: 1px solid var(--gray6);
}

.sust-legend-percentageOther {
  background-color: var(--sust-color-percentageOther);
}
.sust-legend-keyAllineatiAS {
  background-color: var(--sust-color-keyAllineatiAS);
}
.sust-legend-percentageOtherCharacteristicsInvestment {
  background-color: var(--sust-color-percentageOtherCharacteristicsInvestment);
}
.sust-legend-keySostenibili {
  background-color: var(--sust-color-keySostenibili);
}
.sust-legend-percentageOtherSocialInvestment {
  background-color: var(--sust-color-percentageOtherSocialInvestment);
}
.sust-legend-percentageOtherEnvironmentalInvestments {
  background-color: var(--sust-color-percentageOtherEnvironmentalInvestments);
}
.sust-legend-percentageOtherTaxonomyAlignedInvestments {
  background-color: var(--sust-color-percentageOtherTaxonomyAlignedInvestments);
}
.sust-legend-percentageNotDefined {
  background-color: var(--sust-color-percentageNotDefined);
}

.sust-chart-legend {
  font-weight: 500;
}

.mt-legend {
  margin-top: 2rem;
}
</style>
